import AnimationWrapper from '@AnimationWrapper';
import InfoPanel from '@InfoPanel';
import DOMPurify from 'isomorphic-dompurify';
import React from 'react';
import striptags from 'striptags';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import { isDOMPurifySupported } from '../Html/HtmlHelpers';
import { swapS3DomainWithPublicAssetsDomain } from '../__helpers__/component.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';

const Hint = ({ title, content, iconUrl, componentId }) => {
  const theme = useTheme();
  let Icon;
  let sanitisedText;

  const animationExperimentName = useGetExperimentName({
    componentId,
    componentName: 'AnimationWrapper',
  });
  const infoPanelExperimentName = useGetExperimentName({
    componentId,
    componentName: 'InfoPanel',
  });

  const unsafeProtocols = [
    /^javascript:/i,
    /^data:/i,
    /^vbscript:/i,
    /^file:/i,
    /^blob:/i,
    /^filesystem:/i,
    /^ws:/i,
    /^wss:/i,
    /^ftp:/i,
    /^about:/i,
  ];

  if (!isDOMPurifySupported()) {
    // If DOMPurify is not supported, strip all tags from the string to prevent script injection.
    sanitisedText = striptags(content);
  } else {
    // DOMPurify strips all tags other than those specified as well as any attributes such as onload.
    sanitisedText = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['i', 'strong', 'b', 'a', 'br', 'ol', 'ul', 'li'],
      ALLOWED_ATTR: ['href'], // Allow href attribute
      FORBID_ATTR: ['onclick', 'onload', 'onerror'], // Forbid JS attributes
      FORBID_TAGS: ['script'], // Forbid script tags
    });

    // Additional precaution: Strip any hrefs that contain unsafe protocols
    const unsafeProtocolsRegex = new RegExp(
      unsafeProtocols.map((protocol) => protocol.source).join('|'),
      'gi'
    );
    sanitisedText = sanitisedText.replace(/href="[^"]*"/g, (match) => {
      const url = match.slice(6, -1); // Get the URL part of href="url"
      return unsafeProtocolsRegex.test(url) ? 'href="#"' : match;
    });
  }

  if (iconUrl) {
    const strippedUrl = swapS3DomainWithPublicAssetsDomain(
      iconUrl,
      process.env.NEXT_PUBLIC_ASSETS_URL
    );

    Icon = <img src={strippedUrl} alt="hintImage" />;
  }

  return (
    <AnimationWrapper
      effect="fadeInUp"
      delay={600}
      animationTime={600}
      experimentName={animationExperimentName}
    >
      <Box
        marginBottom={theme.mvf.spacing.small}
        marginTop={theme.mvf.spacing.small}
      >
        <InfoPanel
          title={title}
          Icon={Icon}
          experimentName={infoPanelExperimentName}
        >
          {/* eslint-disable react/no-danger */}
          {/* dangerouslySetInnerHTML should only be used with sanitised HTML text. */}
          <span dangerouslySetInnerHTML={{ __html: sanitisedText }} />
        </InfoPanel>
      </Box>
    </AnimationWrapper>
  );
};

export default Hint;
